import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs, updateDoc, increment } from "firebase/firestore";
import { db } from "../../useFirebase"; // Ensure this path is correct
import "./styles.css";
import Footer from "components/Footer/Footer";
import ScrollToTop from "components/ScrollToTop";

/**
 * Blog
 * Rendering the blog post page based on the new design.
 */
export default function Blog() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [cvData, setCvData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        let found = null;
        let actualDocId = null;
        // 1. Try fetching by ID directly from 'blogs_new'
        const docRef = doc(db, "blogs_new", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          found = { id: docSnap.id, ...docSnap.data() };
          actualDocId = docSnap.id;
        } else {
          // Fallback: If document ID is not the ID, but there is an 'id' field.
          const q = query(collection(db, "blogs_new"), where("id", "==", parseInt(id)));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const d = querySnapshot.docs[0];
            found = { id: d.id, ...d.data() };
            actualDocId = d.id;
          } else {
            // Try string ID just in case
            const q2 = query(collection(db, "blogs_new"), where("id", "==", id));
            const querySnapshot2 = await getDocs(q2);
            if (!querySnapshot2.empty) {
              const d = querySnapshot2.docs[0];
              found = { id: d.id, ...d.data() };
              actualDocId = d.id;
            }
          }
        }

        if (found && actualDocId) {
          // Check if blog is in the future (scheduled post)
          const getDate = (d) => (d?.toDate ? d.toDate() : new Date(d));
          if (getDate(found.createdAt) > new Date()) {
            console.log("Future blog post not allowed to view yet");
            setPost(null);
            setLoading(false);
            return;
          }

          // Increment viewcount in Firestore for internal stats
          const postRef = doc(db, "blogs_new", actualDocId);
          updateDoc(postRef, {
            viewcount: increment(1)
          }).catch((error) => {
            console.error("Failed to increment viewcount in Firestore:", error);
          });

          // 2. Map data fields
          const [companyName, authorName] = found.title && found.title.includes("|")
            ? found.title.split("|").map((s) => s.trim())
            : [found.company || "Company", found.name_ || found.author || "Author"];

          const mappedPost = {
            ...found,
            company: found.company || companyName,
            jobProfile: found.role || found.title || "Role",
            author: found.name_ || found.author || authorName,
            date: found.createdAt,
            content: found.contentHtml || found.content || `<p>${found.description}</p>`,
            category: found.category || "Software",
            type: found.type || (found.category === 'internship' ? 'Internship' : 'Placement'),
            roleOffered: found.roleOffered || found.jobProfile || found.category || "Role",
            department: found.department || "IIT Kharagpur",
            authorImage: found.cover,
            companyLogo: found.companyLogo
          };

          setPost(mappedPost);

          // 3. Conditional CV Fetching if cvDocId exists
          if (mappedPost.cvDocId) {
            const cvRef = doc(db, "cvs_new", mappedPost.cvDocId);
            const cvSnap = await getDoc(cvRef);
            if (cvSnap.exists()) {
              setCvData(cvSnap.data());
            }
          }
        } else {
          console.log("Blog post not found for ID:", id);
        }
      } catch (error) {
        console.error("Error fetching blog post:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPost();
    }
  }, [id]);

  if (loading) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading...</div>;
  if (!post) return <div style={{ padding: '50px', textAlign: 'center', minHeight: '50vh' }}><h1>Blog Post Not Found</h1><Link to="/blog">Go Back</Link></div>;

  // Use fetched CV cover or mapped post cvImage if available
  const resumeImage = cvData?.hasResume ? cvData.cover : post.cvImage;

  return (
    <>
      <div className="bp-page" >
        {/* ─── Main Content Column ─── */}
        <main className="bp-main">
          {/* ── Title + Meta ── */}
          <section className="bp-meta">
            <h1 className="bp-meta__title">{post.company}</h1>
            <p className="bp-meta__subtitle">{post.jobProfile}</p>

            <div className="bp-meta__ribbon">
              <span className="bp-meta__tag">
                <svg viewBox="0 0 16 16" width="14" height="14"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M8 4v4l3 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                {post.type}
              </span>
              <span className="bp-meta__tag">
                <svg viewBox="0 0 16 16" width="14" height="14"><rect x="1" y="3" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M1 7h14" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
                {post.roleOffered}
              </span>
            </div>

            <ul className="bp-meta__details">
              <li><strong>Department</strong> {cvData?.dept || post.department}</li>
              <li><strong>Author</strong> {post.author}</li>
              <li><strong>Published</strong> {new Date(post.date).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
              })}
              </li>
            </ul>
          </section>

          {/* ── Author Photo ── */}
          {post.authorImage && (
            <figure className="bp-author-photo">
              <img src={post.authorImage} alt={post.author} />
              <figcaption>{post.author} — {cvData?.dept || post.department}</figcaption>
            </figure>
          )}

          {/* ── Article Body ── */}
          <article
            className="bp-article"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* ── CV / Resume Image ── */}
          {resumeImage && (
            <section className="bp-cv">
              <h3 className="bp-cv__heading">CV</h3>
              <p className="bp-cv__note">
                A well-structured CV is crucial during CDC season. Below is the resume
                that was used to secure this role.
              </p>
              <figure className="bp-cv__figure">
                <img src={resumeImage} alt="Sample CV" />
                <figcaption>Resume used during {post.company} CDC drive</figcaption>
              </figure>
            </section>
          )}

          {/* ── Back Link ── */}
          <Link to={post.type === "internship" ? "/internblogs" : "/placementblogs"} className="bp-back">
            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M19 12H5M12 19l-7-7 7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Back to {post.type === "internship" ? "Internship" : "Placement"} blogs
          </Link>
        </main>
      </div>
      <Footer />
      <ScrollToTop />
    </>
  );
}
